import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, Heart, Clock, CheckCircle2, XCircle, ArrowUpRight, FolderKanban, Pencil, Trash2, KeyRound, GitPullRequestArrow } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import AdminProjectModal from "../components/AdminProjectModal";

const STATUS_STYLE = {
  pending: "border-amber-300 bg-amber-50 text-amber-600",
  approved: "border-emerald-300 bg-emerald-50 text-emerald-600",
  rejected: "border-rose-300 bg-rose-50 text-rose-500",
};

const inputCls =
  "w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-[#2563EB] focus:bg-white";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400";

function AccountPanel({ t }) {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { name: form.name, email: form.email };
      if (form.current_password || form.new_password) {
        payload.current_password = form.current_password;
        if (form.new_password) payload.new_password = form.new_password;
      }
      const r = await api.put("/auth/account", payload);
      localStorage.setItem("kmu_token", r.data.token);
      setUser(r.data.user);
      setForm({ ...form, current_password: "", new_password: "" });
      toast.success(t.acct_updated);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const emailChanged = form.email.trim().toLowerCase() !== (user?.email || "").toLowerCase();
  const needsPw = emailChanged || !!form.new_password;

  return (
    <div className="rounded-xl border border-slate-200/90 bg-white p-6 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)] sm:p-7" data-testid="account-panel">
      <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900"><KeyRound size={17} className="text-[#2563EB]" /> {t.my_account_tab}</h3>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls}>{t.auth_name}</label>
          <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name-input" />
        </div>
        <div>
          <label className={labelCls}>{t.auth_email}</label>
          <input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="account-email-input" />
        </div>
        <div>
          <label className={labelCls}>{t.creds_current}{needsPw ? " *" : ""}</label>
          <input type="password" autoComplete="current-password" className={inputCls} value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="account-current-password" />
        </div>
        <div>
          <label className={labelCls}>{t.acct_new_pw}</label>
          <input type="password" autoComplete="new-password" className={inputCls} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="account-new-password" />
        </div>
      </div>
      <button onClick={save} disabled={busy || !form.name.trim() || !form.email.trim() || (needsPw && !form.current_password)} data-testid="account-save-button" className="mt-5 rounded-full bg-slate-900 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#1E3A8A] disabled:cursor-not-allowed disabled:opacity-40">
        {busy ? t.creds_saving : t.news_update}
      </button>
    </div>
  );
}

export default function MyProjectsPage() {
  const { user, checking } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [tab, setTab] = useState("projects");
  const [editTarget, setEditTarget] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  useEffect(() => {
    if (!checking && !user) navigate("/auth?next=my", { replace: true });
  }, [checking, user, navigate]);

  const load = useCallback(() => {
    if (user) api.get(`/projects/mine?lang=${lang}`).then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, [user, lang]);

  useEffect(() => { load(); }, [load]);

  if (checking || !user || projects === null) return null;

  const counts = {
    pending: projects.filter((p) => p.status === "pending").length,
    approved: projects.filter((p) => p.status === "approved").length,
    rejected: projects.filter((p) => p.status === "rejected").length,
  };

  const STATUS_META = {
    pending: { label: t.tab_pending, Icon: Clock },
    approved: { label: t.tab_approved, Icon: CheckCircle2 },
    rejected: { label: t.tab_rejected, Icon: XCircle },
  };

  const withdraw = async (id) => {
    if (confirmId !== id) { setConfirmId(id); setTimeout(() => setConfirmId(null), 3000); return; }
    try {
      await api.delete(`/projects/${id}/mine`);
      toast.success("Submission withdrawn");
      setConfirmId(null);
      setProjects(projects.filter((p) => p.id !== id));
    } catch {
      toast.error("Withdraw failed");
    }
  };

  return (
    <main className="mx-auto max-w-5xl px-5 pb-24 pt-28 lg:px-10" data-testid="my-projects-page">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#2563EB]">{t.login_tag}</p>
      <h1 className="mt-2 font-display text-3xl font-bold text-slate-900">{t.my_title}</h1>
      <p className="mt-1 text-sm text-slate-500">{t.my_sub}</p>

      <div className="mt-7 flex gap-2" data-testid="my-tabs">
        {[["projects", t.my_projects_tab], ["account", t.my_account_tab]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`my-tab-${key}`} className={`rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-300 ${tab === key ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-slate-200 bg-white text-slate-500 hover:border-[#2563EB]/50 hover:text-[#1E3A8A]"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "account" && <div className="mt-8"><AccountPanel t={t} /></div>}

      {tab === "projects" && (
        <>
          <div className="mt-7 flex flex-wrap gap-2.5" data-testid="my-status-summary">
            {Object.entries(STATUS_META).map(([key, { label, Icon }]) => (
              <span key={key} data-testid={`my-count-${key}`} className={`flex items-center gap-1.5 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLE[key]}`}>
                <Icon size={12} /> {label} · {counts[key]}
              </span>
            ))}
          </div>

          {projects.length === 0 ? (
            <div className="mt-8 flex flex-col items-center rounded-xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center" data-testid="my-empty-state">
              <FolderKanban size={28} className="text-slate-300" />
              <p className="mt-4 text-sm text-slate-500">{t.my_empty}</p>
              <button onClick={() => navigate("/?open_submit=1")} data-testid="my-submit-cta" className="mt-6 rounded-full bg-[#1E3A8A] px-6 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#2563EB]">
                {t.hero_cta2}
              </button>
            </div>
          ) : (
            <div className="mt-8 space-y-4" data-testid="my-project-list">
              {projects.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex flex-col gap-5 rounded-xl border border-slate-200/90 bg-white p-5 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)] sm:flex-row"
                  data-testid={`my-project-${p.id}`}
                >
                  <img src={resolveImage(p.image_url)} alt={p.title} className="h-28 w-full rounded-lg object-cover sm:w-44" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] ${STATUS_STYLE[p.status]}`} data-testid={`my-status-${p.id}`}>
                        {STATUS_META[p.status]?.label || p.status}
                      </span>
                      {p.pending_edit && (
                        <span className="flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-amber-600" data-testid={`my-edits-pending-${p.id}`}>
                          <GitPullRequestArrow size={10} /> {t.my_edits_pending}
                        </span>
                      )}
                      <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-400">{p.department_label || p.department}</span>
                      <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-400">{t.my_submitted} {(p.created_at || "").slice(0, 10)}</span>
                    </div>
                    <h3 className="mt-2 font-display text-base font-semibold text-slate-900">{p.title}</h3>
                    {p.tagline && <p className="mt-1 text-xs text-slate-500">{p.tagline}</p>}
                    <div className="mt-2 flex items-center gap-4 font-mono text-[10px] text-slate-400">
                      <span className="flex items-center gap-1"><Eye size={11} /> {p.views}</span>
                      <span className="flex items-center gap-1"><Heart size={11} /> {p.likes}</span>
                    </div>
                    {p.status === "rejected" && p.feedback && (
                      <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-xs leading-relaxed text-rose-600" data-testid={`my-feedback-${p.id}`}>
                        <span className="font-mono text-[9px] uppercase tracking-[0.15em]">{t.my_feedback}:</span> {p.feedback}
                      </p>
                    )}
                    <div className="mt-4 flex flex-wrap items-center gap-2.5">
                      <button onClick={() => setEditTarget(p)} data-testid={`my-edit-${p.id}`} className="flex items-center gap-1.5 rounded-md border border-slate-200 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-600 transition-colors hover:border-[#2563EB]/50 hover:text-[#1E3A8A]">
                        <Pencil size={12} /> {t.news_edit_btn}
                      </button>
                      {p.status === "approved" && (
                        <button onClick={() => navigate("/projects")} data-testid={`my-view-gallery-${p.id}`} className="flex items-center gap-1.5 rounded-md border border-emerald-300 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-600 transition-colors hover:bg-emerald-600 hover:text-white">
                          {t.my_view_gallery} <ArrowUpRight size={12} />
                        </button>
                      )}
                      <button onClick={() => withdraw(p.id)} data-testid={`my-withdraw-${p.id}`} className={`flex items-center gap-1.5 rounded-md border px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${confirmId === p.id ? "border-rose-500 bg-rose-500 text-white" : "border-slate-200 text-slate-400 hover:border-rose-300 hover:text-rose-500"}`}>
                        <Trash2 size={12} /> {confirmId === p.id ? t.act_sure : t.my_withdraw}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      {editTarget && (
        <AdminProjectModal
          project={editTarget}
          endpoint={`/projects/${editTarget.id}/mine`}
          startEditing
          isAdmin={false}
          onClose={() => setEditTarget(null)}
          onSaved={(updated) => {
            setEditTarget(null);
            setProjects(projects.map((p) => (p.id === updated.id ? updated : p)));
          }}
        />
      )}
    </main>
  );
}
